<template>
	<div class="super-clicker-box">
		<div class="manual-tooltip-wrapper">
			<div
				class="manual-tooltip-popper"
				v-if="superClickersUsed <= 0 && showUseSuperClickerTooltip"
			>
				<div class="body">
					<div class="content">
						{{
							translate(`tutorial.${ResourceType.SUPER_CLICKER}`)
						}}
					</div>
				</div>
				<div
					class="v-popper__arrow-container data-popper-placement-left"
				>
					<div class="v-popper__arrow-outer"></div>
					<div class="v-popper__arrow-inner"></div>
				</div>
			</div>
		</div>
		<Tooltip
			v-if="state === 'default'"
			:heading="translate(`resource.${ResourceType.SUPER_CLICKER}`)"
			customWidth="16rem"
		>
			<template #element>
				<!-- Default State: 10x with value -->
				<div
					class="circle clicker-box-off"
					:class="[{ 'pointer-events-none': isCameraActive }]"
					@click="
						superClickerCount > 0 ? activate('activated') : null
					"
				>
					<div class="state-value">
						{{
							`${
								10 +
								getUpgradeCurrentReplaceValueById(
									UpgradeIds.butterfingers,
								)
							}x`
						}}
					</div>
					<div class="circle-value">
						{{ formatNumber(superClickerCount) }}
					</div>
				</div>
			</template>
			<template #tooltip>
				<span class="super-clicker-box__tooltip">
					{{
						translate(
							`resource.${ResourceType.SUPER_CLICKER}_description`,
						).replace(
							"{number}",
							(
								SuperClickerEnum.TEN +
								getUpgradeCurrentReplaceValueById(
									UpgradeIds.butterfingers,
								)
							).toString(),
						)
					}}
					{{
						translate(
							`resource.${ResourceType.SUPER_CLICKER}_description_2`,
						)
					}}
				</span>
			</template>
		</Tooltip>
		<!-- Activated State: 10x activated/deactivated -->
		<div
			v-else-if="state === 'activated'"
			class="circle clicker-box-10x"
			@click="deactivate"
		>
			<div class="state-value">
				{{
					`${
						10 +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.butterfingers,
						)
					}x`
				}}
			</div>
			<div class="circle-value">
				{{ formatNumber(superClickerCount) }}
			</div>
			<div class="activated-badge">{{ translate("ui.active") }}</div>
		</div>
		<Tooltip
			v-else
			:heading="translate('ui.click_multiplier')"
			customWidth="16rem"
		>
			<template #element>
				<!-- 2x Activated State -->
				<div v-if="state === '2x'" class="circle clicker-box-2x">
					<div class="state-value">{{ state }}</div>
					<div class="circle-value">
						{{ formatNumber(clicker2Count) }}
					</div>
					<div class="activated-badge">
						{{ translate("ui.active") }}
					</div>
				</div>

				<!-- 3x Activated State -->
				<div v-if="state === '3x'" class="circle clicker-box-3x">
					<div class="state-value">{{ state }}</div>
					<div class="circle-value">
						{{ formatNumber(clicker3Count) }}
					</div>
					<div class="activated-badge">
						{{ translate("ui.active") }}
					</div>
				</div>

				<!-- 5x Activated State -->
				<div v-if="state === '5x'" class="circle clicker-box-5x">
					<div class="state-value">{{ state }}</div>
					<div class="circle-value">
						{{ formatNumber(clicker5Count) }}
					</div>
					<div class="activated-badge">
						{{ translate("ui.active") }}
					</div>
				</div>
			</template>
			<template #tooltip>
				<span class="super-clicker-box__tooltip">
					{{ translate("ui.click_multiplier_description") }}
				</span>
			</template>
		</Tooltip>
	</div>
</template>

<script lang="ts" setup>
import Tooltip from "@/components/Tooltip.vue";
import { ResourceType } from "@/game/entities/config/resources";
import { UpgradeIds } from "@/game/entities/config/upgrades";
import { getUpgradeCurrentReplaceValueById } from "@/game/utils/upgrades";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore, { SuperClickerEnum } from "@/store/useRuntimeStore";
import useUserStore from "@/store/useUserStore";
import { formatNumber } from "@/utils/format";
import { translate } from "@/utils/translate";

import { storeToRefs } from "pinia";
import { computed, onMounted, ref, watch } from "vue";

const userStore = useUserStore();
const { superClickersUsed } = storeToRefs(userStore);
const runtimeStore = useRuntimeStore();
const { isCameraActive, showUseSuperClickerTooltip } =
	storeToRefs(runtimeStore);

type SuperClickerState = "default" | "activated" | "2x" | "3x" | "5x";

const state = ref<SuperClickerState>("default");

const resourcesStore = useResourcesStore();
const superClickerCount = computed(() => resourcesStore.superClicker);
const clicker2Count = computed(() => resourcesStore.clicker2);
const clicker3Count = computed(() => resourcesStore.clicker3);
const clicker5Count = computed(() => resourcesStore.clicker5);

const activate = (newState: SuperClickerState): void => {
	const hasClickers = getClickerCount(newState) > 0;
	runtimeStore.setSuperClicker(true);
	if (hasClickers) {
		state.value = newState;
	}
};

const deactivate = (): void => {
	runtimeStore.setSuperClicker(false);
	state.value = "default";
};

const getClickerCount = (clickerState: SuperClickerState): number => {
	switch (clickerState) {
		case "2x":
			return clicker2Count.value;
		case "3x":
			return clicker3Count.value;
		case "5x":
			return clicker5Count.value;
		case "activated":
		default:
			return superClickerCount.value;
	}
};

onMounted(() => {
	if (clicker2Count.value > 0) {
		activate("2x");
		runtimeStore.setSuperClickerType(SuperClickerEnum.TWO);
	} else if (clicker3Count.value > 0) {
		activate("3x");
		runtimeStore.setSuperClickerType(SuperClickerEnum.THREE);
	} else if (clicker5Count.value > 0) {
		activate("5x");
		runtimeStore.setSuperClickerType(SuperClickerEnum.FIVE);
	} else {
		deactivate();
	}
	runtimeStore.setSuperClickerInitialized(true);
});

watch(
	() => superClickerCount.value,
	(newCount: number) => {
		if (newCount <= 0 && state.value !== "default") {
			deactivate();
		}
	},
);

watch(
	() => clicker2Count.value,
	(newCount: number) => {
		if (newCount <= 0 && state.value !== "default") {
			deactivate();
			runtimeStore.setSuperClickerType(SuperClickerEnum.TEN);
		} else if (newCount > 0 && state.value !== "2x") {
			activate("2x");
			runtimeStore.setSuperClickerType(SuperClickerEnum.TWO);
		}
	},
);

watch(
	() => clicker3Count.value,
	(newCount: number) => {
		if (newCount <= 0 && state.value !== "default") {
			deactivate();
			runtimeStore.setSuperClickerType(SuperClickerEnum.TEN);
		} else if (newCount > 0 && state.value !== "3x") {
			activate("3x");
			runtimeStore.setSuperClickerType(SuperClickerEnum.THREE);
		}
	},
);

watch(
	() => clicker5Count.value,
	(newCount: number) => {
		if (newCount <= 0 && state.value !== "default") {
			deactivate();
			runtimeStore.setSuperClickerType(SuperClickerEnum.TEN);
		} else if (newCount > 0 && state.value !== "5x") {
			activate("5x");
			runtimeStore.setSuperClickerType(SuperClickerEnum.FIVE);
		}
	},
);
</script>

<style lang="scss" scoped>
$clickerTypes: (
	"off": 0,
	"10x": 1,
	"2x": 2,
	"3x": 3,
	"5x": 4,
);
$last_index: 0;
@each $name, $index in $clickerTypes {
	$last_index: $index;
}

@each $clicker, $pos in $clickerTypes {
	.clicker-box-#{$clicker} {
		display: inline-block;
		background-image: url($path-icons + "shop/clickers-ui-sprite.avif");
		background-position: (-(3.938) * $pos) * 1rem 0;
		background-size: (3.938) * ($last_index + 1) * 1rem;
		background-repeat: no-repeat;
		width: 3.938rem;
		height: 4.313rem;
	}
}

.super-clicker-box {
	display: flex;
	justify-content: end;

	.circle {
		display: flex;
		position: relative;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		cursor: pointer;
		pointer-events: auto;

		&.pointer-events-none {
			pointer-events: none;
		}

		&:hover {
			transform: scale(1.05);
		}

		&:active {
			transform: scale(0.95);
		}

		&.disabled {
			opacity: 0.9;
			cursor: not-allowed;

			&:hover {
				transform: none;
			}
		}
	}

	.circle-value {
		position: absolute;
		bottom: 0.75rem;
		width: 100%;
		color: #fff3dd;
		font-size: 0.713rem;
		text-align: center;
	}

	.state-value {
		position: absolute;
		bottom: 1.5rem;
		color: #f2dcb5;
		font-weight: bold;
		font-size: 1rem;
		letter-spacing: normal;
		text-shadow: 0.044rem 0.044rem 0.063rem #31313a;
	}

	.activated-badge {
		position: absolute;
		bottom: -0.5rem;
		background: linear-gradient(
			90deg,
			rgba(17, 17, 17, 0) 0%,
			rgba(17, 17, 17, 0.6) 25%,
			rgba(17, 17, 17, 0.6) 75%,
			rgba(17, 17, 17, 0) 100%
		);
		padding: 0.05rem 0.8rem;
		color: #fff3dd;
		font-weight: bold;
		font-size: 0.625rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
	}
	.clicker-box-10x {
		.activated-badge {
			background: linear-gradient(
				90deg,
				rgba(43, 25, 56, 0) 0%,
				rgba(97, 45, 128, 0.8) 25%,
				rgba(155, 64, 206, 0.8) 50%,
				rgba(97, 45, 128, 0.8) 75%,
				rgba(43, 25, 56, 0) 100%
			);
		}
	}
	&__tooltip {
		text-align: center;
	}

	.manual-tooltip-wrapper {
		@include flex-col-center;
		z-index: 0;
		margin-right: 1rem;
		.manual-tooltip-popper {
			position: relative;
			box-shadow: inset 0 0 0 0.125rem rgba(255, 255, 255, 0.1);
			border-radius: 0.15rem;
			background: url($path-images + "backgrounds/textures/window-texture-bg.avif")
				repeat center;
			background-size: 90% auto;
			padding: 0.125rem;
			width: auto;
			min-width: 7rem;
			height: fit-content;

			.header {
				@include flex-row-center;
				padding: 0.25rem;
				width: 100%;

				&-container {
					@include flex-col-center;
				}

				.heading {
					position: relative;
					z-index: 1;
					padding-bottom: 0.25rem;
					color: #f4d278;
					font-weight: 800;
					font-size: 0.875rem;
					text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
					text-transform: uppercase;
				}
			}

			.body {
				background: #f2dcb5;
				width: 100%;

				&::before {
					position: absolute;
					top: 0;
					left: 0;
					opacity: 1 !important;
					// NOTE: Multiply blending is better for light colors
					mix-blend-mode: multiply !important;
					background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
						repeat center;
					background-size: 60%;
					width: 100%;
					height: 100%;
					content: "";
				}

				.content {
					@include flex-col-center;
					position: relative;
					z-index: 1;
					padding: 0.5rem;
					min-height: 1.375rem;

					span {
						color: #372615;
						font-size: 0.75rem;
					}
				}
			}
			.v-popper__arrow-container {
				// base style for arrow
				::after {
					position: absolute;
					left: 50%;
					width: 0.875rem;
					height: 0.563rem;
					content: "";
					@include background(
						url($path-icons + "menu/tooltip-arrow.avif"),
						contain,
						center,
						no-repeat
					);

					z-index: 2;
				}

				.v-popper__arrow-outer {
					border-color: transparent !important;
					background: transparent !important;
				}

				&.data-popper-placement-left {
					top: calc(50% - 0.625rem);
					right: -0.5rem;
					::after {
						transform: translateX(-50%) rotate(90deg);
					}
				}
			}
		}
	}
}
</style>
